import React, { useMemo } from 'react';

export default function PersonnelStep({ data, value, onChange, combinedData }) {
    // 1. Determine Data Source:
    // In Wizard context, 'data' is the flattened location data, so 'data.personnel' is the correct, fresh list from Firestore.
    const personnel = data.personnel || [];

    // Calculate Effective Risk Level (Allow Override)
    const effectiveRiskLevel = value.riskLevelOverride ? parseInt(value.riskLevelOverride) : (combinedData?.riskLevel || 1);

    // Extract personnel by role
    const personnelByRole = useMemo(() => {
        const byRole = {
            werkvoorbereiders: [],
            werkverantwoordelijken: [],
            vp: [],
            vop: [],
            all: []
        };

        personnel.forEach(person => {
            const roleName = person.Functie;
            if (!roleName) return;

            byRole.all.push(roleName);

            // Robust Check: Handle 'Ja', 'x', true (boolean), 'true' (string), and trim whitespace.
            const checkRole = (val) => {
                if (!val) return false;
                if (val === true) return true;
                if (typeof val === 'string') {
                    const s = val.trim();
                    if (s === 'Nee') return false;
                    return s === 'Ja' || s === 'x' || s === 'true';
                }
                return false;
            };

            const isWV = checkRole(person.Rol_WV);
            const isWVB = checkRole(person.Werkvoorbereider);
            const isVP = checkRole(person.Rol_VP);
            const isVOP = checkRole(person.Rol_VOP);

            if (isWV) byRole.werkverantwoordelijken.push(roleName);
            if (isWVB) byRole.werkvoorbereiders.push(roleName);
            if (isVP) byRole.vp.push(roleName);
            if (isVOP) byRole.vop.push(roleName);
        });

        return byRole;
    }, [personnel]);

    const handleChange = (field, val) => {
        onChange({ ...value, [field]: val });
    };

    // Get qualification level for a person/role
    const getQualification = (roleName) => {
        const person = personnel.find(p => p.Functie === roleName);
        if (!person) return 'Onbekend';

        const check = (val) => {
            if (!val) return false;
            if (val === true) return true;
            if (typeof val === 'string') {
                const s = val.trim();
                return s === 'Ja' || s === 'x' || s === 'true';
            }
            return false;
        };

        if (check(person.Rol_VP)) return 'VP';
        if (check(person.Rol_VOP)) return 'VOP';
        if (check(person.Rol_WV)) return 'WV';
        if (check(person.Rol_IV)) return 'IV';
        return 'OP';
    };

    // Get minimum qualification based on EFFECTIVE Risk Level (Override or Base)
    // Advice Implemented: The text now updates dynamically to reflect the chosen risk level.
    const getMinimumQualification = () => {
        if (effectiveRiskLevel === 3) return 'VP';
        if (effectiveRiskLevel === 2) return 'VOP';
        return 'Geen';
    };

    const minimumQualification = getMinimumQualification();
    const nen3140Required = minimumQualification !== 'Geen' && minimumQualification !== '';

    return (
        <div className="fade-in space-y-6">
            <h2 className="text-xl font-bold mb-4 text-white border-b border-slate-700 pb-2">Stap 6: Functierollen</h2>

            {/* Risk Level Indicator & Override */}
            <div className="bg-slate-800/50 p-3 rounded border border-slate-700 mb-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-2">
                    <span className="text-slate-400 text-sm">Validatie Risiconiveau (Berekend: Niveau {combinedData?.riskLevel || 1}):</span>
                    <div className="flex items-center gap-2">
                        <select
                            className="select select-xs bg-slate-800 border-slate-600 text-white"
                            value={value.riskLevelOverride || ''}
                            onChange={(e) => handleChange('riskLevelOverride', e.target.value ? parseInt(e.target.value) : null)}
                        >
                            <option value="">Automatisch (Niveau {combinedData?.riskLevel || 1})</option>
                            <option value="1">Handmatig: Niveau 1 (Geen)</option>
                            <option value="2">Handmatig: Niveau 2 (VOP)</option>
                            <option value="3">Handmatig: Niveau 3 (VP)</option>
                        </select>

                        <span className={`font-bold px-3 py-1 rounded text-sm ${effectiveRiskLevel === 3 ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                            effectiveRiskLevel === 2 ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' :
                                'bg-green-500/20 text-green-400 border border-green-500/30'
                            }`}>
                            Niveau {effectiveRiskLevel}
                        </span>
                    </div>
                </div>

                {/* Risk Level Explanations */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2 text-[10px] text-slate-400 bg-slate-900/30 p-2 rounded">
                    <div className={`p-1 rounded ${effectiveRiskLevel === 1 ? 'bg-green-500/10 border border-green-500/20' : 'opacity-50'}`}>
                        <span className="font-bold text-green-400 block mb-1">Niveau 1 (Geen)</span>
                        Geen specifiek elektrisch risico. Geen speciale bevoegdheid vereist.
                    </div>
                    <div className={`p-1 rounded ${effectiveRiskLevel === 2 ? 'bg-orange-500/10 border border-orange-500/20' : 'opacity-50'}`}>
                        <span className="font-bold text-orange-400 block mb-1">Niveau 2 (VOP)</span>
                        Voldoende Onderricht Persoon. Beperkte elektrische handelingen toegestaan.
                    </div>
                    <div className={`p-1 rounded ${effectiveRiskLevel === 3 ? 'bg-red-500/10 border border-red-500/20' : 'opacity-50'}`}>
                        <span className="font-bold text-red-400 block mb-1">Niveau 3 (VP)</span>
                        Vakbekwaam Persoon. Volledige bevoegdheid voor complexe elektrische taken.
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                {/* Werkvoorbereider */}
                <div>
                    <label className="label">Werkvoorbereider (WV)</label>
                    <select
                        className="input glass-input w-full p-2 rounded border border-slate-600 bg-slate-800 text-white"
                        value={value.werkvoorbereider || ''}
                        onChange={(e) => handleChange('werkvoorbereider', e.target.value)}
                    >
                        <option value="" className="bg-slate-800">Selecteer Werkvoorbereider...</option>
                        {personnelByRole.werkvoorbereiders.map(name => (
                            <option key={name} value={name} className="bg-slate-800">{name}</option>
                        ))}
                    </select>
                </div>

                {/* Werkverantwoordelijke */}
                <div>
                    <label className="label">Werkverantwoordelijke (eindverantwoordelijk)</label>
                    <select
                        className="input glass-input w-full p-2 rounded border border-slate-600 bg-slate-800 text-white"
                        value={value.werkverantwoordelijke || ''}
                        onChange={(e) => handleChange('werkverantwoordelijke', e.target.value)}
                    >
                        <option value="" className="bg-slate-800">Selecteer Werkverantwoordelijke...</option>
                        {personnelByRole.werkverantwoordelijken.map(name => (
                            <option key={name} value={name} className="bg-slate-800">{name}</option>
                        ))}
                    </select>
                </div>

                {/* NEN3140 Info */}
                <div className="card glass-panel border-blue-500/30 bg-blue-900/10 p-4 rounded border">
                    <h3 className="font-bold text-sm mb-2 text-blue-400">📋 NEN3140 Vereisten</h3>
                    <div className="text-sm space-y-1 text-slate-300">
                        <div>NEN3140 toewijzing vereist: <span className="font-bold text-green-400">{nen3140Required ? 'Ja' : 'Nee'}</span></div>
                        <div>Minimale kwalificatie: <span className="font-bold text-yellow-400">{minimumQualification}</span></div>
                    </div>
                </div>

                {/* Uitvoerende Rol 1 */}
                <div>
                    <label className="label">Uitvoerende Rol 1 (Selecteer Persoon) *</label>
                    <select
                        className="input glass-input w-full p-2 rounded border border-slate-600 bg-slate-800 text-white"
                        value={value.assignedUserId || ''}
                        onChange={(e) => {
                            const selectedUsername = e.target.value;
                            const user = data.users?.find(u => u.username === selectedUsername);
                            const displayName = user ? (user.name || user.username) : '';

                            onChange({
                                ...value,
                                assignedUserId: selectedUsername,
                                monteur1: displayName
                            });
                        }}
                    >
                        <option value="" className="bg-slate-800">Selecteer Uitvoerder...</option>
                        {(data.users || [])
                            .map(user => {
                                const linkedRole = user.linkedPersonnelRoleId ? `(${user.linkedPersonnelRoleId})` : '(Geen rol gekoppeld)';
                                const display = user.name ? `${user.name} ${linkedRole}` : `${user.username} ${linkedRole}`;
                                return (
                                    <option key={user.username} value={user.username} className="bg-slate-800">
                                        {display}
                                    </option>
                                );
                            })}
                    </select>

                    {value.assignedUserId && data.users && (
                        <div className="mt-4">
                            {(() => {
                                const user = data.users.find(u => u.username === value.assignedUserId);
                                if (!user) return null;

                                const roleName = user.linkedPersonnelRoleId;
                                if (roleName) {
                                    const currentQual = getQualification(roleName);

                                    // Validation against EFFECTIVE Risk Level
                                    let isValid = true;

                                    // Level 1: Everyone OK
                                    // Level 2: VOP or above OK
                                    // Level 3: VP or above OK
                                    const qualLevels = { 'OP': 1, 'IV': 2, 'WV': 3, 'VOP': 2, 'VP': 3 };
                                    const userLevel = qualLevels[currentQual] || 1;

                                    if (userLevel < effectiveRiskLevel) {
                                        isValid = false;
                                    }

                                    if (!isValid) {
                                        return (
                                            <div className="p-4 rounded-lg bg-red-900/40 border-2 border-red-500 flex items-start gap-4 animate-pulse">
                                                <div className="text-3xl">⛔</div>
                                                <div>
                                                    <h4 className="text-red-400 font-bold text-lg uppercase tracking-wider">Toegang Geweigerd</h4>
                                                    <p className="text-slate-300 text-sm mt-1">
                                                        Deze taak vereist <strong>Niveau {effectiveRiskLevel}</strong> bevoegdheid.
                                                        <br />
                                                        Geselecteerde gebruiker ({user.name}) heeft: <strong className="text-red-300">{currentQual}</strong> (Niveau {userLevel}).
                                                    </p>
                                                    <div className="mt-2 text-xs text-red-400 font-mono bg-black/30 p-1 rounded inline-block">
                                                        Hard-Block Actief: Volgende stap geblokkeerd.
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div className="p-3 rounded border bg-green-500/10 border-green-500/30 flex items-center gap-3">
                                            <div className="text-xl">✅</div>
                                            <div>
                                                <span className="text-green-400 font-bold block">Bevoegd</span>
                                                <span className="text-slate-400 text-xs">
                                                    Kwalificatie: {currentQual} ({roleName}) matches Niveau {effectiveRiskLevel}.
                                                </span>
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                    <div className="p-3 rounded border border-yellow-500/30 bg-yellow-500/10 text-yellow-200 text-sm flex items-center gap-2">
                                        <span>⚠️</span>
                                        <span>Let op: Geen functierol gekoppeld aan deze gebruiker. Bevoegdheid kan niet worden geverifieerd.</span>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>

                {/* Uitvoerende Rol 2 */}
                <div>
                    <label className="label">Uitvoerende Rol 2 (Optioneel)</label>
                    <select
                        className="input glass-input w-full p-2 rounded border border-slate-600 bg-slate-800 text-white"
                        value={value.monteur2_username || ''}
                        onChange={(e) => {
                            const selectedUsername = e.target.value;
                            const user = data.users?.find(u => u.username === selectedUsername);
                            const displayName = user ? (user.name || user.username) : '';

                            onChange({
                                ...value,
                                monteur2: displayName,
                                monteur2_username: selectedUsername
                            });
                        }}
                    >
                        <option value="" className="bg-slate-800">Geen 2e uitvoerder</option>
                        {(data.users || [])
                            .map(user => {
                                const linkedRole = user.linkedPersonnelRoleId ? `(${user.linkedPersonnelRoleId})` : '';
                                const display = user.name ? `${user.name} ${linkedRole}` : `${user.username} ${linkedRole}`;
                                return (
                                    <option key={user.username} value={user.username} className="bg-slate-800">
                                        {display}
                                    </option>
                                );
                            })}
                    </select>
                </div>

            </div>
        </div>
    );
}
